import { useCallback, useEffect, useRef, useState } from 'react';

export interface FanZoneData {
  id: number;
  name: string;
  deposits: number;
  rank: number;
  collectorId?: string;
}

interface FanZoneWebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
}

export const useFanZoneWebSockets = (initialFanZones: FanZoneData[]) => {
  const [fanZones, setFanZones] = useState<FanZoneData[]>(initialFanZones);
  const [websocketStates, setWebsocketStates] = useState<Record<number, FanZoneWebSocketState>>({});
  const websocketRefs = useRef<Record<number, WebSocket>>({});
  const reconnectTimeouts = useRef<Record<number, NodeJS.Timeout>>({});
  const pingIntervals = useRef<Record<number, NodeJS.Timeout>>({});

  const updateFanZoneDeposits = useCallback((fanZoneId: number, deposits: number) => {
    setFanZones(prev => {
      const updated = prev.map(fanZone =>
        fanZone.id === fanZoneId
          ? { ...fanZone, deposits }
          : fanZone
      );

      // Recalculate rankings based on deposit count (descending order)
      const sorted = [...updated].sort((a, b) => b.deposits - a.deposits);
      return sorted.map((fanZone, index) => ({
        ...fanZone,
        rank: index + 1
      }));
    });
  }, []);

  const updateWebSocketState = useCallback((fanZoneId: number, state: Partial<FanZoneWebSocketState>) => {
    setWebsocketStates(prev => ({
      ...prev,
      [fanZoneId]: {
        ...prev[fanZoneId],
        ...state
      }
    }));
  }, []);

  const connectFanZone = useCallback((fanZoneId: number, collectorId: string) => {
    const url = `${process.env.REACT_APP_WEBSOCKET_BASE_API_URL}/deposits/count/${collectorId}`;

    if (websocketRefs.current[fanZoneId]) {
      websocketRefs.current[fanZoneId].close();
      delete websocketRefs.current[fanZoneId];
    }

    if (reconnectTimeouts.current[fanZoneId]) {
      clearTimeout(reconnectTimeouts.current[fanZoneId]);
      delete reconnectTimeouts.current[fanZoneId];
    }

    if (pingIntervals.current[fanZoneId]) {
      clearInterval(pingIntervals.current[fanZoneId]);
      delete pingIntervals.current[fanZoneId];
    }

    updateWebSocketState(fanZoneId, {
      isConnecting: true,
      error: null
    });

    try {
      const ws = new WebSocket(url);
      websocketRefs.current[fanZoneId] = ws;

      // Connection timeout to detect stuck connections
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
          setTimeout(() => connectFanZone(fanZoneId, collectorId), 1000);
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        updateWebSocketState(fanZoneId, {
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectAttempts: 0
        });

        pingIntervals.current[fanZoneId] = setInterval(() => {
          try {
            if (websocketRefs.current[fanZoneId]?.readyState === WebSocket.OPEN) {
              websocketRefs.current[fanZoneId].send("ping");
            }
          } catch (e) {
            console.error(`[WS][FanZone ${fanZoneId}] Error sending ping`, e);
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          if (!event.data || event.data.trim() === '') {
            return;
          }

          if (event.data === 'ping' || event.data === 'pong') {
            return;
          }

          let deposits: number = 0;
          let data: any;

          try {
            data = JSON.parse(event.data);
          } catch (jsonError: any) {
            const numValue = parseFloat(event.data);
            if (!isNaN(numValue)) {
              deposits = numValue;
            } else {
              return;
            }
          }

          if (data !== undefined) {
            if (typeof data === 'number') {
              deposits = data;
            } else if (data && typeof data === 'object' && 'value' in data) {
              deposits = data.value as number;
            } else {
              return;
            }
          }

          if (typeof deposits !== 'number' || isNaN(deposits)) {
            return;
          }

          const normalized = Math.floor(deposits);
          updateFanZoneDeposits(fanZoneId, normalized);
        } catch (error) {
          console.warn(`[WS][FanZone ${fanZoneId}] Error procesando mensaje:`, error);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        
        if (pingIntervals.current[fanZoneId]) {
          clearInterval(pingIntervals.current[fanZoneId]);
          delete pingIntervals.current[fanZoneId];
        }
        
        updateWebSocketState(fanZoneId, {
          isConnected: false,
          isConnecting: false
        });

        if (event.code !== 1000 && event.code !== 1006) {
          setWebsocketStates(prev => {
            const currentState = prev[fanZoneId];
            if (currentState && currentState.reconnectAttempts < 5) {
              const newAttempts = (currentState.reconnectAttempts || 0) + 1;

              const delay = Math.min(1000 * Math.pow(2, newAttempts - 1), 10000);
              reconnectTimeouts.current[fanZoneId] = setTimeout(() => {
                connectFanZone(fanZoneId, collectorId);
              }, delay);

              return {
                ...prev,
                [fanZoneId]: {
                  ...currentState,
                  reconnectAttempts: newAttempts,
                  error: `Reconnecting... (${newAttempts}/5)`
                }
              };
            } else {
              return {
                ...prev,
                [fanZoneId]: {
                  ...currentState,
                  error: 'Max reconnect attempts reached'
                }
              };
            }
          });
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error(`[WS][FanZone ${fanZoneId}] WebSocket error:`, error);
        updateWebSocketState(fanZoneId, {
          isConnected: false,
          isConnecting: false,
          error: 'Connection error'
        });
      };

    } catch (error) {
      console.error(`[WS][FanZone ${fanZoneId}] Error creating WebSocket:`, error);
      updateWebSocketState(fanZoneId, {
        isConnected: false,
        isConnecting: false,
        error: `Error creating WebSocket: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  }, [updateFanZoneDeposits, updateWebSocketState, websocketStates]);

  const disconnectFanZone = useCallback((fanZoneId: number) => {
    if (reconnectTimeouts.current[fanZoneId]) {
      clearTimeout(reconnectTimeouts.current[fanZoneId]);
      delete reconnectTimeouts.current[fanZoneId];
    }

    if (pingIntervals.current[fanZoneId]) {
      clearInterval(pingIntervals.current[fanZoneId]);
      delete pingIntervals.current[fanZoneId];
    }

    if (websocketRefs.current[fanZoneId]) {
      websocketRefs.current[fanZoneId].close(1000, 'Disconnected by user');
      delete websocketRefs.current[fanZoneId];
    }

    updateWebSocketState(fanZoneId, {
      isConnected: false,
      isConnecting: false,
      error: null,
      reconnectAttempts: 5
    });
  }, [updateWebSocketState]);

  const connectAll = useCallback(() => {
    const source = fanZones.length > 0 ? fanZones : initialFanZones;
    
    source.forEach((fanZone, index) => {
      if (fanZone.collectorId && !websocketStates[fanZone.id]?.isConnected) {
        setTimeout(() => {
          connectFanZone(fanZone.id, fanZone.collectorId!);
        }, index * 100);
      }
    });
  }, [fanZones, initialFanZones, websocketStates, connectFanZone]);

  const disconnectAll = useCallback(() => {
    fanZones.forEach(fanZone => {
      disconnectFanZone(fanZone.id);
    });
  }, [fanZones, disconnectFanZone]);

  // Sync internal state when initialFanZones changes
  useEffect(() => {
    setFanZones(prev => {
      const prevById = new Map(prev.map(f => [f.id, f]));
      const nextById = new Map(initialFanZones.map(f => [f.id, f]));

      // Disconnect websockets for removed fan zones
      prev.forEach(f => {
        if (!nextById.has(f.id)) {
          disconnectFanZone(f.id);
        }
      });

      // Merge: keep deposits from existing, update meta from incoming
      const merged = initialFanZones.map(f => {
        const existing = prevById.get(f.id);
        const deposits = existing ? existing.deposits : 0;
        return { ...f, deposits, rank: 0 } as FanZoneData;
      });

      const sorted = [...merged].sort((a, b) => b.deposits - a.deposits);
      return sorted.map((f, idx) => ({ ...f, rank: idx + 1 }));
    });
  }, [initialFanZones, disconnectFanZone]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all reconnection timeouts
      Object.values(reconnectTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });

      Object.values(pingIntervals.current).forEach(interval => {
        clearInterval(interval);
      });

      // Close all WebSocket connections
      Object.values(websocketRefs.current).forEach(ws => {
        ws.close();
      });
    };
  }, []);

  return {
    fanZones,
    websocketStates,
    connectAll,
    disconnectAll
  };
};
